import ExcelJS from "exceljs";
import sealImage from "../assets/img/ht-seal.png";
import { formatCurrency } from "./utils";

// Hotel Trader company details rendered at the top of the reconciliation report.
// These are static company identifiers used by client finance teams to reconcile.
export const COMPANY_DETAILS = {
  name: "Hotel Trader Inc.",
  address: "800 North State Street, Suite 304, Dover, DE 19901",
  email: "accountsreceivable@hoteltrader.com",
  taxId: "39-3987903",
};

const SHEET_NAME = "Bookings";
const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;

/**
 * Parse a CSV string into an array of row arrays, honoring quoted fields
 * (which may contain commas, escaped quotes, or newlines).
 * @param {string} csvText
 * @returns {string[][]}
 */
const parseCsv = (csvText) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];

    if (inQuotes) {
      if (char === '"') {
        if (csvText[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (char !== "\r") {
      field += char;
    }
  }

  // Flush trailing field/row
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop fully empty rows (e.g. a trailing newline)
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ""));
};

// Convert a CSV cell to a number when it is purely numeric so Excel treats
// amounts as numbers (not text), while leaving IDs/dates/codes as strings.
const coerceCell = (value) =>
  NUMERIC_PATTERN.test(value.trim()) ? Number(value) : value;

// Locate the (already-renamed) "Transaction Amount" column, ignoring case and
// separators, so the compliance total stays in sync with the exported column.
const findTransactionAmountIndex = (headerRow) =>
  headerRow.findIndex(
    (header) =>
      String(header).trim().toLowerCase().replace(/[\s_]/g, "") ===
      "transactionamount"
  );

// Sum of the Transaction Amount column. Refund rows are already negated by
// transformExportCsv, so this yields the net total shown to client finance.
const computeTotalTransactionAmount = (headerRow, dataRows) => {
  const amountIdx = findTransactionAmountIndex(headerRow);
  if (amountIdx < 0) return 0;
  return dataRows.reduce((sum, cells) => {
    const raw = (cells[amountIdx] || "").trim();
    const num = parseFloat(raw);
    return isNaN(num) ? sum : sum + num;
  }, 0);
};

// Header blocks are laid out across columns (matching the sample report):
// From=C, To/Recipient=D, Beneficiary Account=E, Total + Report Filters=F.
const COLUMN = { FROM: 3, TO: 4, BENEFICIARY: 5, SUMMARY: 6, GENERATED: 8 };

const setCell = (worksheet, row, col, text, style) => {
  const cell = worksheet.getCell(row, col);
  cell.value = text;
  if (style) cell.style = style;
};

// Write a vertical stack of [text, style] pairs down a single column starting at
// row 1. Empty pairs act as spacer rows. Returns the number of rows written.
const writeColumnBlock = (worksheet, col, lines) => {
  lines.forEach(([text, style], idx) =>
    setCell(worksheet, idx + 1, col, text, style)
  );
  return lines.length;
};

// "Generated on" timestamp in UTC, formatted as YYYY-MM-DD HH:mmUTC per the
// sample report (e.g. "2026-02-01 14:32UTC").
const formatGeneratedOnUtc = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}UTC`
  );
};

// Column D: "To" / Recipient Details from the invoice schedule (via ht-admin).
// Only sections with data are emitted; the caller omits the whole block when no
// recipient is available (per IO-142).
const buildRecipientLines = (recipient, labelStyle) => {
  const lines = [["To", labelStyle], [recipient.name || ""]];
  if (recipient.address) {
    lines.push(["Address", labelStyle]);
    lines.push([recipient.address]);
  }
  if (recipient.taxId) {
    lines.push(["Tax Id", labelStyle]);
    lines.push([recipient.taxId]);
  }
  return lines;
};

// Column F: total transaction amount, then (only-applied) report filters.
const buildSummaryLines = (
  { currency, appliedFilters = [] },
  totalTransactionAmount,
  labelStyle
) => {
  const currencyLabel = currency ? ` (${currency})` : "";
  const lines = [
    [`Total Transaction Amount${currencyLabel}`, labelStyle],
    [
      formatCurrency(totalTransactionAmount, currency) ||
        `${totalTransactionAmount}`,
    ],
  ];

  // Only surface filters the user actually applied (ticket: skip unused ones).
  if (appliedFilters.length > 0) {
    lines.push([""]);
    lines.push(["Report Filters", labelStyle]);
    appliedFilters.forEach(({ label, value }) => {
      lines.push([`${label}: ${value}`]);
    });
  }

  return lines;
};

/**
 * Build a branded XLSX workbook (company header + seal + bookings table) from
 * the transformed bookings CSV.
 * @param {string} csvText transformed bookings CSV (headers on the first row)
 * @param {Object} [reportMeta] compliance details for the report header
 * @param {string} [reportMeta.currency] currency code for amount formatting
 * @param {{name: string, address: string, taxId: string}} [reportMeta.recipient]
 *        invoice-schedule recipient for the "To" block (section skipped if absent)
 * @param {Array<{label: string, value: string}>} [reportMeta.appliedFilters]
 *        only the filters the user actually applied
 * @param {Date} [reportMeta.generatedOn] report generation timestamp
 * @returns {Promise<Blob>} XLSX file as a Blob
 */
export const buildBookingsReport = async (csvText, reportMeta = {}) => {
  const rows = parseCsv(csvText || "");
  const headerRow = rows[0] || [];
  const dataRows = rows.slice(1);
  const columnCount = Math.max(headerRow.length, 1);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = COMPANY_DETAILS.name;
  const worksheet = workbook.addWorksheet(SHEET_NAME);

  // --- Header blocks laid out across columns (seal in A/B): From=C, To=D,
  // Beneficiary Account=E, Total + Report Filters=F, Generated on top-right. ---
  const labelStyle = { font: { bold: true } };
  const { currency, appliedFilters = [], generatedOn, recipient } = reportMeta;

  const fromLines = [
    ["From", labelStyle],
    [COMPANY_DETAILS.name],
    ["Address", labelStyle],
    [COMPANY_DETAILS.address],
    ["Email", labelStyle],
    [COMPANY_DETAILS.email],
    ["Tax Id", labelStyle],
    [COMPANY_DETAILS.taxId],
  ];
  writeColumnBlock(worksheet, COLUMN.FROM, fromLines);

  // "To" / Recipient Details (column D) from the invoice schedule — skipped
  // entirely when no recipient details are available (per IO-142). "Beneficiary
  // Account" (column E) is not wired yet.
  const toRows = recipient
    ? writeColumnBlock(
        worksheet,
        COLUMN.TO,
        buildRecipientLines(recipient, labelStyle)
      )
    : 0;

  const totalTransactionAmount = computeTotalTransactionAmount(
    headerRow,
    dataRows
  );
  const summaryLines = buildSummaryLines(
    { currency, appliedFilters },
    totalTransactionAmount,
    labelStyle
  );
  const summaryRows = writeColumnBlock(worksheet, COLUMN.SUMMARY, summaryLines);

  // Generated-on timestamp, top-right and italic, matching the sample report.
  setCell(
    worksheet,
    1,
    COLUMN.GENERATED,
    `Generated on: ${formatGeneratedOnUtc(generatedOn || new Date())}`,
    { font: { italic: true } }
  );

  // --- Company seal (anchored top-left over the header block) ---
  try {
    const response = await fetch(sealImage);
    const buffer = await response.arrayBuffer();
    const imageId = workbook.addImage({ buffer, extension: "png" });
    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 120, height: 120 },
    });
  } catch (error) {
    // The report is still useful without the seal; don't fail the download.
    console.error("Unable to embed company seal in report:", error);
  }

  // --- Bookings table starts below the tallest header column (one spacer row) ---
  const headerRowsUsed = Math.max(fromLines.length, toRows, summaryRows, 1);
  const tableHeaderRowNumber = headerRowsUsed + 2;

  if (headerRow.length > 0) {
    const tableHeader = worksheet.getRow(tableHeaderRowNumber);
    headerRow.forEach((title, colIdx) => {
      const cell = tableHeader.getCell(colIdx + 1);
      cell.value = title;
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF2F2F2" },
      };
      cell.border = { bottom: { style: "thin", color: { argb: "FFBFBFBF" } } };
    });

    dataRows.forEach((cells, rowIdx) => {
      const tableRow = worksheet.getRow(tableHeaderRowNumber + 1 + rowIdx);
      cells.forEach((value, colIdx) => {
        tableRow.getCell(colIdx + 1).value = coerceCell(value);
      });
    });
  }

  // --- Column widths: fit the wider of the header block and the table ---
  for (let col = 0; col < columnCount; col++) {
    let maxLen = headerRow[col] ? String(headerRow[col]).length : 0;
    dataRows.forEach((cells) => {
      const len = cells[col] ? String(cells[col]).length : 0;
      if (len > maxLen) maxLen = len;
    });
    // Column C also holds the company details lines.
    if (col === 2) {
      maxLen = Math.max(maxLen, COMPANY_DETAILS.address.length);
    }
    worksheet.getColumn(col + 1).width = Math.min(Math.max(maxLen + 2, 12), 45);
  }

  const arrayBuffer = await workbook.xlsx.writeBuffer();
  return new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
